import React, { useState } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getErrorMessage } from '../../utils/errorHandler';
import styles from './Register.module.css';

const API_BASE = process.env.REACT_APP_API_BASE_URL || '';

interface RegisterProps {
  embedded?: boolean;
}

const Register: React.FC<RegisterProps> = ({ embedded = false }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [username, setUsername] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const { setUser } = useAuth();
  const navigate = useNavigate();

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    try {
      const response = await axios.post(
        `${API_BASE}/api/auth/register`,
        {
          email,
          password,
          confirmPassword,
          username,
          referralSource: localStorage.getItem('njord_referral') || undefined,
        },
        { withCredentials: true }
      );
      // Backend already logs the user in via req.login(), so proceed directly
      setUser(response.data);
      navigate('/create-mercenary');
      return;
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to create account. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  const formContent = (
    <form onSubmit={handleRegister} className={styles.form}>
      <div className={styles.fieldGroup}>
        <label className={styles.label}>Email</label>
        <input
          className={styles.input}
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="your@email.com"
          required
        />
      </div>
      <div className={styles.fieldGroup}>
        <label className={styles.label}>Username</label>
        <input
          className={styles.input}
          type="text"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          placeholder="Your username"
          required
        />
      </div>
      <div className={styles.fieldGroup}>
        <label className={styles.label}>Password</label>
        <input
          className={styles.input}
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="••••••••"
          required
        />
      </div>
      <div className={styles.fieldGroup}>
        <label className={styles.label}>Confirm Password</label>
        <input
          className={styles.input}
          type="password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          placeholder="••••••••"
          required
        />
      </div>

      {error && <p className={styles.error}>{error}</p>}
      {success && <p className={styles.success}>{success}</p>}

      <button type="submit" className={styles.btnSubmit} disabled={loading}>
        {loading ? 'Creating Account…' : 'Create Account'}
      </button>
    </form>
  );

  if (embedded) {
    return <div className={styles.embeddedForm}>{formContent}</div>;
  }

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        <h1 className={styles.title}>Njordheim</h1>
        <p className={styles.subtitle}>Join the Realm</p>
        <hr className={styles.dividerTop} />

        {formContent}

        <hr className={styles.divider} />
        <p className={styles.footer}>
          Already have an account?{' '}
          <Link to="/login" className={styles.link}>Login here</Link>
        </p>
      </div>
    </div>
  );
};

export default Register;
